import { useSyncExternalStore } from 'react';

export const APP_INSET_RIGHT_CLASS = 'app-inset-right';
export const APP_INSET_RIGHT_VAR = '--app-inset-right';

type Listener = () => void;

const insets = new Map<string, number>();
let insetRight = 0;
const listeners = new Set<Listener>();

const normalizeWidth = (widthPx: number | null): number => {
  const rounded = Math.round(widthPx ?? 0);
  return rounded > 0 ? rounded : 0;
};

const removeAttributeIfEmpty = (element: Element, name: string) => {
  if (element.getAttribute(name)?.trim() === '') {
    element.removeAttribute(name);
  }
};

const applyToDocument = (width: number) => {
  const root = document.documentElement;
  if (width > 0) {
    root.style.setProperty(APP_INSET_RIGHT_VAR, `${width}px`);
    root.classList.add(APP_INSET_RIGHT_CLASS);
  } else {
    root.classList.remove(APP_INSET_RIGHT_CLASS);
    root.style.removeProperty(APP_INSET_RIGHT_VAR);
    removeAttributeIfEmpty(root, 'class');
    removeAttributeIfEmpty(root, 'style');
  }
};

export const setAppInsetRight = (
  owner: string,
  widthPx: number | null
): void => {
  const next = normalizeWidth(widthPx);
  if (next === (insets.get(owner) ?? 0)) {
    return;
  }
  if (next > 0) {
    insets.set(owner, next);
  } else {
    insets.delete(owner);
  }
  const effective = Math.max(0, ...insets.values());
  if (effective !== insetRight) {
    insetRight = effective;
    applyToDocument(effective);
  }
  [...listeners].forEach((listener) => listener());
};

export const getAppInsetRight = (owner?: string): number =>
  owner === undefined ? insetRight : insets.get(owner) ?? 0;

const subscribeAppInsetRight = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const useAppInsetRight = (owner?: string): number => {
  const getSnapshot = () => getAppInsetRight(owner);
  return useSyncExternalStore(subscribeAppInsetRight, getSnapshot, getSnapshot);
};
